import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef } from 'react';

import {
  sliderWrapperStyle,
  sliderTrackBaseStyle,
  slideItemStyle,
  slideIconWrapperStyle,
  slideTitleStyle,
  slideSubtitleStyle,
  dotsWrapperStyle,
  dotBaseStyle,
  dotActiveStyle
} from './styles';

import PriorityLikesShape from '@/components/common/svg/shapes/paywall/PriorityLikesShape';
import FireReactionShape from '@/components/common/svg/shapes/FireReactionShape';
import HeartSimpleShape from '@/components/common/svg/shapes/HeartSimpleShape';
import InfinityShape from '@/components/common/svg/shapes/paywall/InfinityShape';

const slidesData = [
  {
    id: 1,
    icon: <PriorityLikesShape height={25} width={25} />,
    title: 'paywall.benefits.content.priority_likes',
    subtitle: 'paywall.benefits.content.priority_likes.additional'
  },
  {
    id: 2,
    icon: <FireReactionShape height={25} width={25} fillColor="#FDC93F" />,
    title: 'paywall.benefits.content.weekly_reactions',
    subtitle: 'paywall.benefits.content.weekly_reactions.additional'
  },
  {
    id: 3,
    icon: <HeartSimpleShape height={25} width={25} fillColor="#FDC93F" />,
    title: 'paywall.benefits.content.see_who_liked_you',
    subtitle: 'paywall.benefits.content.see_who_liked_you.additional'
  },
  {
    id: 4,
    icon: <InfinityShape height={27} width={27} fillColor="#FDC93F" />,
    title: 'paywall.benefits.content.unlimited_likes',
    subtitle: 'paywall.benefits.content.unlimited_likes.additional'
  }
];

const Slider = () => {
  const { t } = useTranslation();

  const extendedSlides = [
    slidesData[slidesData.length - 1],
    ...slidesData,
    slidesData[0]
  ];

  const [active, setActive] = useState(1);
  const [startX, setStartX] = useState<number | null>(null);
  const [translateX, setTranslateX] = useState(0);
  const [animating, setAnimating] = useState(false);

  const isTouching = useRef(false);

  const autoplayRef = useRef<NodeJS.Timeout | null>(null);

  const normalizeIndex = (index: number) => {
    const len = slidesData.length;
    let normalized = index % (len + 2);
    if (normalized < 0) normalized += len + 2;
    if (normalized === 0) return len - 1;
    if (normalized === len + 1) return 0;
    return normalized - 1;
  };

  const resetAutoplay = () => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
    }

    autoplayRef.current = setInterval(() => {
      if (isTouching.current) return;

      setAnimating(true);
      setActive((prev) => prev + 1);
    }, 3000);
  };

  const handleStart = (e: React.TouchEvent) => {
    if (animating) return;
    setStartX(e.touches[0].clientX);
    setAnimating(false);
    isTouching.current = true;
  };

  const handleMove = (e: React.TouchEvent) => {
    if (startX === null || animating) return;
    setTranslateX(e.touches[0].clientX - startX);
  };

  const handleEnd = () => {
    if (startX === null || animating) return;

    const threshold = 70;
    let newIndex = active;

    if (translateX < -threshold) {
      newIndex = active + 1;
    } else if (translateX > threshold) {
      newIndex = active - 1;
    }

    setAnimating(true);
    setActive(newIndex);

    setTranslateX(0);
    setStartX(null);
    isTouching.current = false;

    resetAutoplay();
  };

  useEffect(() => {
    if (!animating) return;

    const timer = setTimeout(() => {
      setAnimating(false);

      const len = slidesData.length;
      if (active <= 0) {
        setActive(active + len * Math.ceil(-active / len));
      } else if (active >= len + 1) {
        setActive(active - len * Math.floor((active - 1) / len));
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [active, animating]);

  useEffect(() => {
    resetAutoplay();
    return () => {
      if (autoplayRef.current) clearInterval(autoplayRef.current);
    };
  }, []);

  const realIndex = normalizeIndex(active);

  return (
    <div style={sliderWrapperStyle}>
      <div
        style={{
          ...sliderTrackBaseStyle,
          transform: `translateX(calc(${-active * 100}% + ${translateX}px))`,
          transition: animating ? 'transform 0.25s ease' : 'none'
        }}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
      >
        {extendedSlides.map((slide, idx) => (
          <div key={idx} style={slideItemStyle}>
            <div style={slideIconWrapperStyle}>{slide.icon}</div>
            <div style={slideTitleStyle}>{t(slide.title)}</div>
            <div style={slideSubtitleStyle}>{t(slide.subtitle)}</div>
          </div>
        ))}
      </div>

      <div style={dotsWrapperStyle}>
        {slidesData.map((_, idx) => (
          <div
            key={idx}
            style={idx === realIndex ? dotActiveStyle : dotBaseStyle}
          />
        ))}
      </div>
    </div>
  );
};

export default Slider;
